import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Avatar } from '@components/basics/Images/Avatar';
import { Timestamp } from '@components/basics/Timestamp';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, UserActivityDetailFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { webappUriPath } from '@utils/url';
import { UserHoverCardContent } from './UserHoverCardContent';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UserActivityDetail on User {
    id
    artist {
      id
    }
    publicAddress
    webappUri
    username
    avatar {
      id
      url
    }
  }
`);

export const AVATAR_SIZE = 48;

const UserAccessibleLink = styled(AccessibleLink, {
  minWidth: 0,
});

export const UserActivityDetail = ({
  user,
  subText,
  createdAt,
}: {
  user: FragmentType<UserActivityDetailFragmentDoc>;
  subText: string;
  createdAt: number;
}) => {
  const userData = getFragment(UserActivityDetailFragmentDoc, user);

  const { id: userId, username, avatar, artist, publicAddress, webappUri } = userData;

  const hoverCardTrigger = (
    <Container>
      <div className="h-12 w-12 flex-shrink-0 rounded-full [&_a]:h-12 [&_a]:w-12 ">
        <Avatar
          src={avatar?.url}
          username={username}
          size={AVATAR_SIZE}
          borderRadius={1000}
          walletAddress={publicAddress}
          hasBorder
          webappUri={webappUri}
        />
      </div>
      <InfoWrapper>
        <UserAccessibleLink pathnameUri={webappUriPath(webappUri)}>
          <Username>{username}</Username>
        </UserAccessibleLink>
      </InfoWrapper>
    </Container>
  );

  return (
    <Container>
      <UsernameWrapper>
        <UserHoverCard
          userHoverCardContent={<UserHoverCardContent userId={userId} />}
          trigger={hoverCardTrigger}
        />
        {artist && <VerifiedTooltip portal />}
      </UsernameWrapper>

      <div className="absolute flex translate-x-16 translate-y-7 font-base text-base-m font-medium text-neutral500 lg:h-5">
        <p>{subText}</p>
        <span className="timestamp">
          {SPACE}•{SPACE}
        </span>
        <Timestamp timeStamp={createdAt} className="text-base-m font-normal text-neutral500" />
      </div>
    </Container>
  );
};

export const Container = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
  maxWidth: '100%',
  width: 'fit-content',
});

export const InfoWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: 8,
  minWidth: 0,
});

export const UsernameWrapper = styled('div', {
  display: 'flex',
  gap: 4,
  marginTop: 1,
});

export const Username = styled('h3', {
  fontStyle: 'semibold',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  maxWidth: 'unset',

  '@tabletLandscape': {
    base: 'L',
  },
});
